/**
 * Cookie Manager Utility
 * Handles GTM loading/unloading, cookie cleanup, and preference management
 */

const ANALYTICS_COOKIES = [
  '_ga',
  '_gid',
  '_gat',
  '_gat_gtag_UA',
  '_gcl_au',
  '_gcl_aw',
  '_gcl_dc',
  '_gcl_gb',
  '_gac_gb',
  'AMP_TOKEN',
  '__utma',
  '__utmb',
  '__utmc',
  '__utmt',
  '__utmz',
];

/**
 * Check if the ID is a GA4 measurement ID (G-XXXXXXX) vs a GTM container ID (GTM-XXXXXXX)
 */
const isGA4MeasurementId = (id) => id && id.startsWith('G-');

/**
 * Load Google Tag Manager or GA4 gtag.js script depending on the ID type
 */
export const loadGTM = (analyticsId) => {
  if (!analyticsId) {
    console.warn('cookieManager - No analytics ID provided');
    return false;
  }

  // Check if already loaded
  if (window.dataLayer && document.querySelector(`script[src*="${analyticsId}"]`)) {
    return true;
  }

  try {
    const nonce = window.__CSP_NONCE__ || '';

    if (isGA4MeasurementId(analyticsId)) {
      // GA4 Measurement ID (G-XXXXXXX) — load via gtag.js
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', analyticsId);

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${analyticsId}`;
      script.setAttribute('data-gtag-script', 'true');
      if (nonce) {
        script.setAttribute('nonce', nonce);
      }
      document.head.appendChild(script);
    } else {
      // GTM Container ID (GTM-XXXXXXX) — load via gtm.js
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${analyticsId}`;
      script.setAttribute('data-gtm-script', 'true');
      if (nonce) {
        script.setAttribute('nonce', nonce);
      }
      document.head.appendChild(script);

      // GTM noscript iframe (only for GTM, not needed for gtag.js)
      const noscript = document.createElement('noscript');
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${analyticsId}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';
      iframe.setAttribute('data-gtm-iframe', 'true');
      noscript.appendChild(iframe);
      if (document.body.firstChild) {
        document.body.insertBefore(noscript, document.body.firstChild);
      } else {
        document.body.appendChild(noscript);
      }
    }

    return true;
  } catch (error) {
    console.error('cookieManager - Failed to load analytics:', error);
    return false;
  }
};

/**
 * Unload Google Tag Manager or GA4 gtag.js (complete removal)
 */
export const unloadGTM = (analyticsId) => {
  try {
    // 1. Disable tracking
    if (analyticsId) {
      window[`ga-disable-${analyticsId}`] = true;
    }

    // 2. Remove all GTM/gtag script tags
    const scripts = document.querySelectorAll('script[src*="googletagmanager.com"]');
    scripts.forEach(script => {
      script.parentNode?.removeChild(script);
    });

    // 3. Remove GTM noscript iframes
    const iframes = document.querySelectorAll('iframe[src*="googletagmanager.com"]');
    iframes.forEach(iframe => {
      const noscript = iframe.closest('noscript');
      if (noscript) {
        noscript.parentNode?.removeChild(noscript);
      } else {
        iframe.parentNode?.removeChild(iframe);
      }
    });

    // 4. Clear dataLayer and gtag
    if (window.dataLayer) {
      window.dataLayer.length = 0;
      window.dataLayer = undefined;
    }
    if (window.gtag) {
      window.gtag = undefined;
    }

    // 5. Clear all analytics cookies
    clearAnalyticsCookies();

    return true;
  } catch (error) {
    console.error('cookieManager - Failed to unload GTM:', error);
    return false;
  }
};

/**
 * Clear all analytics-related cookies
 */
export const clearAnalyticsCookies = () => {
  const domains = [
    window.location.hostname,
    `.${window.location.hostname}`,
    window.location.hostname.replace(/^www\./, ''),
    `.${window.location.hostname.replace(/^www\./, '')}`
  ];

  const paths = ['/', ''];

  ANALYTICS_COOKIES.forEach(cookieName => {
    // Also check for pattern-based cookies
    if (cookieName.includes('*')) {
      const pattern = cookieName.replace('*', '');
      const allCookies = document.cookie.split(';');
      allCookies.forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        if (name.includes(pattern)) {
          deleteCookie(name, domains, paths);
        }
      });
    } else {
      deleteCookie(cookieName, domains, paths);
    }
  });

  // Also clear any cookies starting with _dc_gtm_, _gat_gtag_
  const allCookies = document.cookie.split(';');
  allCookies.forEach(cookie => {
    const name = cookie.split('=')[0].trim();
    if (name.startsWith('_dc_gtm_') || name.startsWith('_gat_gtag_') || name.startsWith('_gat_UA')) {
      deleteCookie(name, domains, paths);
    }
  });

};

/**
 * Delete a specific cookie across multiple domains and paths
 */
const deleteCookie = (name, domains, paths) => {
  // Try with all combinations of domain and path
  domains.forEach(domain => {
    paths.forEach(path => {
      // With domain
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}; SameSite=None; Secure`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain};`;
      // Without domain (for current domain)
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; SameSite=None; Secure`;
    });
  });
  
  // Additional cleanup - try root domain variations
  const rootDomain = window.location.hostname.split('.').slice(-2).join('.');
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${rootDomain};`;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

/**
 * Get cookie preferences with timestamp validation
 * @param {string} tenantId - Tenant identifier
 * @param {number} reconsentDays - Number of days before re-consent required (default 365)
 */
export const getCookiePreferences = (tenantId, reconsentDays = 365) => {
  const storageKey = `cookieConsent_${tenantId}`;
  const raw = localStorage.getItem(storageKey);

  if (!raw) {
    return null;
  }

  try {
    const data = JSON.parse(raw);
    
    // Check if data has timestamp
    if (data.timestamp) {
      const timestamp = new Date(data.timestamp);
      const now = new Date();
      const daysSinceConsent = (now - timestamp) / (1000 * 60 * 60 * 24);
      
      // Re-consent required after configured days (default 365 for GDPR)
      if (daysSinceConsent > reconsentDays) {
        localStorage.removeItem(storageKey);
        return null;
      }
    }

    return {
      essential: data.essential !== undefined ? data.essential : true,
      analytics: data.analytics !== undefined ? data.analytics : false,
      timestamp: data.timestamp || new Date().toISOString()
    };
  } catch (error) {
    console.error('cookieManager - Failed to parse preferences:', error);
    return null;
  }
};

/**
 * Save cookie preferences with timestamp
 */
export const saveCookiePreferences = (tenantId, preferences) => {
  const storageKey = `cookieConsent_${tenantId}`;
  const data = {
    essential: preferences.essential !== undefined ? preferences.essential : true,
    analytics: preferences.analytics !== undefined ? preferences.analytics : false,
    timestamp: new Date().toISOString()
  };

  try {
    localStorage.setItem(storageKey, JSON.stringify(data));
    window.__cookieConsent = {
      essential: data.essential,
      analytics: data.analytics
    };
    
    return true;
  } catch (error) {
    console.error('cookieManager - Failed to save preferences:', error);
    return false;
  }
};

/**
 * Check if consent has expired
 * @param {string} tenantId - Tenant identifier
 * @param {number} reconsentDays - Number of days before re-consent required (default 365)
 * @returns {boolean} True if consent expired or not found
 */
export const isConsentExpired = (tenantId, reconsentDays = 365) => {
  const preferences = getCookiePreferences(tenantId, reconsentDays);
  return preferences === null;
};

export default {
  loadGTM,
  unloadGTM,
  clearAnalyticsCookies,
  getCookiePreferences,
  saveCookiePreferences,
  isConsentExpired
};
